import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft, Crown, Heart, Star, Users, Mail, Linkedin,
  Dog, Sparkles, Award, Coffee, Bone
} from 'lucide-react';
import { Button } from '../components/ui/button';

// Team Members Data
const TEAM_MEMBERS = [
  {
    id: 'toby',
    name: 'Toby',
    role: 'Chief Barking Officer',
    roleShort: 'CBO',
    image: 'https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/92iyvmuc_Tobi%20CBO%20Puppoto.jpg',
    bio: 'The face of Puppoto! Toby is a Pomeranian-Chihuahua mix (Pomchi) who believes every dog deserves their perfect match. When he\'s not posing for photos, he\'s testing the app\'s treat-tracking features.',
    funFact: 'Can hear a treat bag opening from 3 rooms away',
    icon: Crown,
    color: 'from-[#FF7555] to-[#E5684A]',
    bgColor: 'bg-gradient-to-br from-[#FF7555]/10 to-[#E5684A]/10',
    featured: true,
    isDog: true
  },
  {
    id: 'koko',
    name: 'Koko',
    role: 'Chief Cuddle & Flirt Officer',
    roleShort: 'CCFO',
    image: 'https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/1wnoe6hu_Koko%20Chief%20Cuddle%20Officer%20and%20Flirt.png',
    bio: 'Toby\'s girlfriend and partner in crime. Koko brings the cuddles and keeps morale high at Puppoto HQ. She\'s the official quality assurance tester for all dog-related features.',
    funFact: 'Has never met a belly rub she didn\'t like',
    icon: Heart,
    color: 'from-pink-500 to-rose-500',
    bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50',
    featured: true,
    isDog: true
  },
  {
    id: 'founder',
    name: 'Paul Klerck',
    role: 'Alpha Pack Leader',
    roleShort: 'Founder & CEO',
    image: 'https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/34zna65a_Paul%20Klerck.jpg',
    bio: 'Visionary entrepreneur behind Puppoto and the Lionheart Digital family. Believes that dogs bring people together and that every pup deserves their perfect match.',
    funFact: 'Toby\'s proud human dad',
    icon: Sparkles,
    color: 'from-purple-500 to-indigo-500',
    bgColor: 'bg-gradient-to-br from-purple-50 to-indigo-50',
    featured: false,
    isDog: false
  },
  {
    id: 'kat',
    name: 'Katalin Klerck',
    role: 'Director',
    roleShort: 'Wife of Alpha',
    image: 'https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/vavjh8hp_Katalin%20Director.jpg',
    bio: 'The creative genius behind Puppoto\'s design and user experience. Katalin brings the vision to life and ensures every pixel is pawfect. Also the mastermind behind our 16-language support.',
    funFact: 'Speaks Hungarian to the dogs',
    icon: Star,
    color: 'from-teal-500 to-cyan-500',
    bgColor: 'bg-gradient-to-br from-teal-50 to-cyan-50',
    featured: false,
    isDog: false
  },
  {
    id: 'pippa',
    name: 'Pippa Keddie',
    role: 'Sales Team Leader',
    roleShort: 'Sales',
    image: 'https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/4cc0i44y_Pippa%20Keddie%20Puppoto%20Sales%20Manager.jpg',
    bio: 'Leading our partnerships and sponsor relationships. Pippa connects Puppoto with amazing brands that love dogs as much as we do. Building a team of passionate sales professionals.',
    funFact: 'Has never met a deal she couldn\'t close',
    icon: Award,
    color: 'from-amber-500 to-orange-500',
    bgColor: 'bg-gradient-to-br from-amber-50 to-orange-50',
    featured: false,
    isDog: false
  }
];

const TeamMemberCard = ({ member, index }) => {
  const Icon = member.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className={`relative rounded-3xl overflow-hidden ${member.bgColor} border border-white/50 ${
        member.featured ? 'md:col-span-2 lg:col-span-1' : ''
      }`}
    >
      {/* Featured Badge for Dogs */}
      {member.isDog && (
        <div className="absolute top-4 right-4 z-10">
          <motion.div 
            className={`bg-gradient-to-r ${member.color} text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg`}
            animate={{ rotate: [0, 3, -3, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            {member.roleShort}
          </motion.div>
        </div>
      )}

      {/* Image Section */}
      <div className="relative h-64 overflow-hidden">
        {member.image ? (
          <img 
            src={member.image} 
            alt={member.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${member.color} flex items-center justify-center`}>
            <div className="text-center text-white">
              <Icon className="w-16 h-16 mx-auto mb-2 opacity-80" />
              <span className="text-sm opacity-70">Photo coming soon</span>
            </div>
          </div>
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Name & Role on Image */}
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-white text-2xl font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            {member.name}
          </h3>
          <p className="text-white/90 font-medium">{member.role}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <p className="text-[#7A7571] text-sm leading-relaxed mb-4">
          {member.bio}
        </p>
        
        {/* Fun Fact */}
        <div className="flex items-start gap-2 bg-white/50 rounded-xl p-3">
          <span className="text-lg">{member.isDog ? '🦴' : '💡'}</span>
          <div>
            <span className="text-xs text-[#7A7571] font-medium uppercase tracking-wide">Fun Fact</span>
            <p className="text-sm text-[#1A1817] font-medium">{member.funFact}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const PupTeam = () => {
  const navigate = useNavigate();

  // Separate dogs from humans for proper ordering
  const dogs = TEAM_MEMBERS.filter(m => m.isDog);
  const humans = TEAM_MEMBERS.filter(m => !m.isDog);

  return (
    <div className="min-h-screen bg-[#FDFCF9]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBE8E0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/puppoto')}
            className="flex items-center gap-2 text-[#1A1817] hover:text-[#FF7555] transition"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Back to Puppoto</span>
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-2xl">🐕</span>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Meet the Pack
            </span>
          </div>
          
          <Button
            onClick={() => navigate('/puppoto/join-sales')}
            className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
          >
            Join Us
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#FF7555]/10 via-white to-[#4E876C]/10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF7555]/10 text-[#FF7555] rounded-full text-sm font-medium mb-6">
              <Users className="w-4 h-4" />
              The Puppoto Family
            </span>
            
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Meet the <span className="text-[#FF7555]">Pack</span>
            </h1>
            
            <p className="text-lg text-[#7A7571] max-w-2xl mx-auto">
              Behind every great platform is a great team - and a few very good dogs. 
              Say hello to the humans and hounds making Puppoto pawsible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* The Dogs - Featured Section */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-[#1A1817] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              The Executive Pups 🐕
            </h2>
            <p className="text-[#7A7571]">The real bosses around here</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {dogs.map((member, index) => (
              <TeamMemberCard key={member.id} member={member} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* The Humans */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-[#1A1817] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              The Humans 👋
            </h2>
            <p className="text-[#7A7571]">The ones who keep the treats coming</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {humans.map((member, index) => (
              <TeamMemberCard key={member.id} member={member} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Join the Team CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#FF7555] to-[#E5684A]">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Want to Join the Pack?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            We're always looking for passionate dog lovers to join our sales team. 
            Great commissions, flexible work, and all the puppy pics you can handle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/puppoto/join-sales')}
              size="lg"
              className="bg-white text-[#FF7555] hover:bg-gray-100 rounded-xl font-semibold px-8"
            >
              <Users className="w-5 h-5 mr-2" />
              Join the Sales Team
            </Button>
            <Button
              onClick={() => navigate('/puppoto/partners')}
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 rounded-xl font-semibold px-8"
            >
              Become a Partner
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#EBE8E0] bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm text-[#7A7571]">
            © 2026 Puppoto • Part of the Lionheart Digital family • Made with 🐕 in Australia
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PupTeam;
